/**
 * GameEngine.ts - Main game controller with state machine and game loop
 * Manages scenes, input, audio, and orchestrates all game systems
 */

import {
  GameScene,
  Scene,
  GameInput,
  GameKey,
  EngineConfig,
  EngineState,
} from './types/engine';
import { AudioManager } from './Audio';
import { AudioTrack } from './data/AudioData';

// Default configuration
const DEFAULT_CONFIG: EngineConfig = {
  canvas: null as any,
  baseWidth: 256,
  baseHeight: 240,
  scale: 3,
  targetFPS: 60,
  pixelPerfect: true,
};

// Music for each scene
const SCENE_MUSIC: Partial<Record<GameScene, AudioTrack>> = {
  [GameScene.TITLE]: AudioTrack.TITLE,
  [GameScene.WORLD]: AudioTrack.WORLD_MAP,
  [GameScene.TOWN]: AudioTrack.TOWN,
  [GameScene.DUNGEON]: AudioTrack.DUNGEON,
  [GameScene.BATTLE]: AudioTrack.BATTLE,
  [GameScene.LEVEL_UP]: AudioTrack.LEVEL_UP,
  [GameScene.GAME_OVER]: AudioTrack.GAME_OVER,
};

export class GameEngine {
  private config: EngineConfig;
  private state: EngineState;
  private ctx: CanvasRenderingContext2D;
  private scenes: Map<GameScene, Scene>;
  private currentScene: Scene | null = null;
  private input: GameInput;
  private audio: AudioManager;

  // Timing
  private lastFrameTime: number = 0;
  private accumulator: number = 0;
  private readonly fixedTimeStep: number = 1000 / 60; // 60 FPS

  // Input state tracking
  private keyMap: Map<string, GameKey>;
  private pressedKeys: Set<string> = new Set();

  // Scene transitions
  private transitionState: {
    active: boolean;
    fadeOut: boolean;
    fadeIn: boolean;
    alpha: number;
    targetScene: GameScene | null;
    duration: number;
    elapsed: number;
  } = {
    active: false,
    fadeOut: false,
    fadeIn: false,
    alpha: 0,
    targetScene: null,
    duration: 500,
    elapsed: 0,
  };

  // Scene stack for nested navigation (e.g., world -> menu -> items)
  private sceneStack: GameScene[] = [];

  // Game state data (loaded from contract)
  private gameStateData: any = null;
  private combatStateData: any = null;

  // Pending battle data (for world->battle transition)
  private pendingBattle: {
    enemies: any[];
    zone: string;
    party: any[];
  } | null = null;

  // Pending level up data (for battle->level up transition)
  private pendingLevelUps: Map<number, any[]> | null = null;

  // Previous scene before battle/shop/inn (for returning)
  private returnScene: GameScene | null = null;

  // Current location context (for town/dungeon scenes)
  private currentLocation: string | null = null;

  constructor(canvas: HTMLCanvasElement, config: Partial<EngineConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config, canvas };

    const ctx = canvas.getContext('2d', {
      alpha: false,
      desynchronized: true,
    });

    if (!ctx) throw new Error('Failed to get 2D context');
    this.ctx = ctx;

    // Setup canvas
    this.setupCanvas();

    // Initialize state
    this.state = {
      running: false,
      paused: false,
      currentScene: GameScene.TITLE,
      fps: 60,
      deltaTime: 0,
      frameCount: 0,
    };

    // Initialize input
    this.input = {
      pressed: new Set(),
      justPressed: new Set(),
      justReleased: new Set(),
    };

    // Keyboard mapping (NES-style)
    this.keyMap = new Map([
      ['ArrowUp', GameKey.UP],
      ['ArrowDown', GameKey.DOWN],
      ['ArrowLeft', GameKey.LEFT],
      ['ArrowRight', GameKey.RIGHT],
      ['KeyZ', GameKey.A], // Confirm
      ['KeyX', GameKey.B], // Cancel
      ['Enter', GameKey.START],
      ['Space', GameKey.SELECT],
      // Alternative WASD
      ['KeyW', GameKey.UP],
      ['KeyS', GameKey.DOWN],
      ['KeyA', GameKey.LEFT],
      ['KeyD', GameKey.RIGHT],
    ]);

    this.scenes = new Map();
    this.setupInputListeners();

    // Initialize audio system
    this.audio = new AudioManager({
      masterVolume: 0.7,
      musicVolume: 0.6,
      sfxVolume: 0.5,
      muted: false,
    });
  }

  // =========================================================================
  // Setup
  // =========================================================================

  private setupCanvas(): void {
    const { canvas, baseWidth, baseHeight, scale, pixelPerfect } = this.config;

    canvas.width = baseWidth;
    canvas.height = baseHeight;
    canvas.style.width = `${baseWidth * scale}px`;
    canvas.style.height = `${baseHeight * scale}px`;

    // Pixel-perfect rendering
    if (pixelPerfect) {
      this.ctx.imageSmoothingEnabled = false;
      canvas.style.imageRendering = 'pixelated';
      canvas.style.imageRendering = 'crisp-edges';
    }
  }

  private setupInputListeners(): void {
    window.addEventListener('keydown', this.handleKeyDown.bind(this));
    window.addEventListener('keyup', this.handleKeyUp.bind(this));

    // Prevent default browser behavior for game keys
    window.addEventListener('keydown', (e) => {
      if (this.keyMap.has(e.code)) {
        e.preventDefault();
      }
    });
  }

  // =========================================================================
  // Input Handling
  // =========================================================================

  private handleKeyDown(e: KeyboardEvent): void {
    const gameKey = this.keyMap.get(e.code);
    if (!gameKey) return;

    // Only trigger justPressed once per key press
    if (!this.pressedKeys.has(e.code)) {
      this.input.justPressed.add(gameKey);
      this.pressedKeys.add(e.code);
    }

    this.input.pressed.add(gameKey);
  }

  private handleKeyUp(e: KeyboardEvent): void {
    const gameKey = this.keyMap.get(e.code);
    if (!gameKey) return;

    this.input.pressed.delete(gameKey);
    this.input.justReleased.add(gameKey);
    this.pressedKeys.delete(e.code);
  }

  private clearFrameInput(): void {
    this.input.justPressed.clear();
    this.input.justReleased.clear();
  }

  // =========================================================================
  // Scene Management
  // =========================================================================

  registerScene(sceneType: GameScene, scene: Scene): void {
    this.scenes.set(sceneType, scene);
  }

  /**
   * Switch scene immediately (no transition)
   */
  switchScene(sceneType: GameScene): void {
    // Cleanup current scene
    if (this.currentScene?.cleanup) {
      this.currentScene.cleanup();
    }

    // Get new scene
    const newScene = this.scenes.get(sceneType);
    if (!newScene) {
      throw new Error(`Scene ${sceneType} not registered`);
    }

    // Initialize new scene
    this.currentScene = newScene;
    this.state.currentScene = sceneType;
    this.currentScene.init();

    // Special handling for battle scene - pass pending battle data
    if (sceneType === GameScene.BATTLE && this.pendingBattle) {
      const battleScene = newScene as any;
      if (typeof battleScene.startBattle === 'function') {
        console.log('[GameEngine] Starting battle with pending data:', this.pendingBattle);
        battleScene.startBattle(
          this.pendingBattle.enemies,
          this.pendingBattle.party,
          this.pendingBattle.zone
        );
        this.pendingBattle = null; // Clear after use
      }
    }

    // Change music if needed
    this.updateMusic(sceneType);
  }

  /**
   * Switch scene with fade transition
   */
  switchSceneWithTransition(sceneType: GameScene, duration: number = 500): void {
    if (this.transitionState.active) return; // Already transitioning

    this.transitionState = {
      active: true,
      fadeOut: true,
      fadeIn: false,
      alpha: 0,
      targetScene: sceneType,
      duration,
      elapsed: 0,
    };
  }

  /**
   * Set scene (alias for switchScene)
   */
  setScene(scene: GameScene): void {
    this.switchSceneWithTransition(scene);
  }

  getCurrentScene(): GameScene {
    return this.state.currentScene;
  }

  // =========================================================================
  // Game Loop
  // =========================================================================

  start(): void {
    if (this.state.running) return;

    this.state.running = true;
    this.lastFrameTime = performance.now();

    // Only auto-switch to title scene if no scene is already set
    if (!this.currentScene && this.scenes.has(GameScene.TITLE)) {
      this.switchScene(GameScene.TITLE);
    }

    requestAnimationFrame(this.gameLoop.bind(this));
  }

  stop(): void {
    this.state.running = false;
  }

  pause(): void {
    this.state.paused = true;
  }

  resume(): void {
    this.state.paused = false;
  }

  private gameLoop(currentTime: number): void {
    if (!this.state.running) return;

    // Calculate delta time
    const deltaTime = currentTime - this.lastFrameTime;
    this.lastFrameTime = currentTime;
    this.accumulator += deltaTime;

    // Update FPS counter
    this.state.fps = Math.round(1000 / deltaTime);

    // Fixed timestep updates
    while (this.accumulator >= this.fixedTimeStep) {
      if (!this.state.paused && this.currentScene) {
        this.update(this.fixedTimeStep);
      }
      this.accumulator -= this.fixedTimeStep;
    }

    // Render
    if (this.currentScene) {
      this.render();
    }

    // Clear frame-specific input
    this.clearFrameInput();

    // Next frame
    this.state.frameCount++;
    requestAnimationFrame(this.gameLoop.bind(this));
  }

  private update(deltaTime: number): void {
    this.state.deltaTime = deltaTime;

    // Update transitions
    if (this.transitionState.active) {
      this.updateTransition(deltaTime);
    }

    // Handle global input (menu toggle)
    this.handleGlobalInput();

    // Update current scene (unless fully faded out)
    if (this.currentScene && (!this.transitionState.active || !this.transitionState.fadeOut || this.transitionState.alpha < 1)) {
      this.currentScene.handleInput(this.input);
      this.currentScene.update(deltaTime);
    }
  }

  /**
   * Handle global input like menu toggle
   */
  private handleGlobalInput(): void {
    // START key opens/closes menu (except during battle or transitions)
    if (this.input.justPressed.has(GameKey.START)) {
      const currentScene = this.state.currentScene;

      // Don't open menu during battle or transitions
      if (this.transitionState.active || currentScene === GameScene.BATTLE) {
        return;
      }

      // Toggle menu
      if (currentScene === GameScene.MENU) {
        this.closeMenu();
      } else if (currentScene === GameScene.WORLD || currentScene === GameScene.TOWN || currentScene === GameScene.DUNGEON) {
        this.openMenu();
      }
    }
  }

  private render(): void {
    // Clear screen with black background (FF1 style)
    this.ctx.fillStyle = '#000000';
    this.ctx.fillRect(0, 0, this.config.baseWidth, this.config.baseHeight);

    // Render current scene
    if (this.currentScene) {
      this.currentScene.render(this.ctx);
    }

    // Render transition overlay
    if (this.transitionState.active && this.transitionState.alpha > 0) {
      this.renderTransition();
    }

    // Debug info (optional)
    // if (process.env.NODE_ENV === 'development') {
    //   this.renderDebugInfo();
    // }
  }

  private updateTransition(deltaTime: number): void {
    this.transitionState.elapsed += deltaTime;
    const progress = Math.min(1, this.transitionState.elapsed / this.transitionState.duration);

    if (this.transitionState.fadeOut) {
      // Fade out current scene
      this.transitionState.alpha = progress;

      if (progress >= 1) {
        // Switch to target scene
        if (this.transitionState.targetScene) {
          this.switchScene(this.transitionState.targetScene);
        }

        // Start fade in
        this.transitionState.fadeOut = false;
        this.transitionState.fadeIn = true;
        this.transitionState.elapsed = 0;
      }
    } else if (this.transitionState.fadeIn) {
      // Fade in new scene
      this.transitionState.alpha = 1 - progress;

      if (progress >= 1) {
        // Transition complete
        this.transitionState.active = false;
        this.transitionState.alpha = 0;
      }
    }
  }

  private renderTransition(): void {
    this.ctx.save();
    this.ctx.fillStyle = '#000000';
    this.ctx.globalAlpha = this.transitionState.alpha;
    this.ctx.fillRect(0, 0, this.config.baseWidth, this.config.baseHeight);
    this.ctx.restore();
  }

  // =========================================================================
  // Debug
  // =========================================================================

  private renderDebugInfo(): void {
    this.ctx.save();
    this.ctx.font = '8px monospace';
    this.ctx.fillStyle = '#00ff00';
    this.ctx.textAlign = 'left';
    this.ctx.textBaseline = 'top';

    const info = [
      `FPS: ${this.state.fps}`,
      `Scene: ${this.state.currentScene}`,
      `Frame: ${this.state.frameCount}`,
    ];

    info.forEach((text, i) => {
      this.ctx.fillText(text, 2, 2 + i * 10);
    });

    this.ctx.restore();
  }

  // =========================================================================
  // Getters
  // =========================================================================

  getContext(): CanvasRenderingContext2D {
    return this.ctx;
  }

  getCanvas(): HTMLCanvasElement {
    return this.config.canvas;
  }

  getState(): Readonly<EngineState> {
    return this.state;
  }

  getInput(): Readonly<GameInput> {
    return this.input;
  }

  getConfig(): Readonly<EngineConfig> {
    return this.config;
  }

  // =========================================================================
  // Game State Management
  // =========================================================================

  /**
   * Load game state from contract
   */
  loadGameState(gameState: any): void {
    this.gameStateData = gameState;
    // Game state would be passed to appropriate scenes (WorldScene, MenuScene, etc.)
  }

  /**
   * Load combat state from contract
   */
  loadCombatState(combatState: any): void {
    this.combatStateData = combatState;
    // Combat state would be passed to BattleScene
  }

  /**
   * Get current game state data
   */
  getGameState(): any {
    return this.gameStateData;
  }

  /**
   * Get current game state with live position from active scene
   * @returns GameState with synced position, or null if no game state
   */
  getCurrentGameState(): any {
    if (!this.gameStateData) {
      return null;
    }

    // Clone the base state
    const state = { ...this.gameStateData };

    // Sync position from active scene
    const worldScene = this.scenes.get(GameScene.WORLD);
    if (worldScene && 'getPosition' in worldScene) {
      const position = (worldScene as any).getPosition();
      if (position) {
        state.worldX = position.x;
        state.worldY = position.y;
      }
    }

    // If in town, get town position
    const townScene = this.scenes.get(GameScene.TOWN);
    if (this.state.currentScene === GameScene.TOWN && townScene && 'getPosition' in townScene) {
      // Town position is relative to town map, not world map
      // Keep world position as-is (where player entered town)
    }

    // If in dungeon, get dungeon floor
    const dungeonScene = this.scenes.get(GameScene.DUNGEON);
    if (this.state.currentScene === GameScene.DUNGEON && dungeonScene && 'getPosition' in dungeonScene) {
      const dungeonPos = (dungeonScene as any).getPosition();
      if (dungeonPos && 'floor' in dungeonPos) {
        state.dungeonFloor = dungeonPos.floor;
      }
    }

    return state;
  }

  /**
   * Get current combat state data
   */
  getCombatState(): any {
    return this.combatStateData;
  }

  /**
   * Check if game is paused
   */
  isPaused(): boolean {
    return this.state.paused;
  }

  /**
   * Check if transitioning
   */
  isTransitioning(): boolean {
    return this.transitionState.active;
  }

  /**
   * Transition to battle scene with encounter data
   */
  startBattle(enemies: any[], zone: string, party: any[]): void {
    // Store battle data for when battle scene initializes
    this.pendingBattle = { enemies, zone, party };

    // Transition to battle scene
    this.switchSceneWithTransition(GameScene.BATTLE, 300);
  }

  /**
   * Get pending battle data (for BattleScene initialization)
   */
  getPendingBattle(): { enemies: any[]; zone: string; party: any[] } | null {
    const battle = this.pendingBattle;
    this.pendingBattle = null; // Clear after retrieval
    return battle;
  }

  /**
   * Get pending level up data (for LevelUpScene initialization)
   */
  getPendingLevelUps(): Map<number, any[]> | null {
    return this.pendingLevelUps;
  }

  /**
   * Return to world scene after battle
   */
  returnToWorld(battleResult: any): void {
    console.log('[GameEngine] Returning to world. Battle result:', battleResult);

    // Apply battle results to game state
    if (battleResult.victory) {
      console.log(`[GameEngine] Victory! Gained ${battleResult.expGained} EXP, ${battleResult.pasGained} PAS`);

      // Check if any characters leveled up
      if (battleResult.levelUps && battleResult.levelUps.size > 0) {
        // Store level up data for later (level up scene not implemented yet)
        this.pendingLevelUps = battleResult.levelUps;
        console.log('[GameEngine] Level ups stored (scene not implemented, returning to world)');
        // TODO: Implement level up scene - for now just log level ups
        for (const [charId, stats] of battleResult.levelUps) {
          console.log(`[GameEngine] Character ${charId} leveled up:`, stats);
        }
      }
    } else if (battleResult.fled) {
      console.log('[GameEngine] Fled from battle');
    } else {
      console.log('[GameEngine] Defeat - returning to world');
    }

    // Always transition back to world
    console.log('[GameEngine] Transitioning to WORLD scene...');
    this.switchSceneWithTransition(GameScene.WORLD, 300);
  }

  /**
   * Return to world after level up scene
   */
  returnToWorldFromLevelUp(): void {
    console.log('[GameEngine] Returning to world after level ups');
    this.pendingLevelUps = null;
    this.switchSceneWithTransition(GameScene.WORLD, 300);
  }

  // =========================================================================
  // Scene Stack Management (for nested menus)
  // =========================================================================

  /**
   * Push current scene to stack and switch to new scene
   */
  pushScene(newScene: GameScene): void {
    if (this.currentScene) {
      this.sceneStack.push(this.state.currentScene);
      console.log(`[GameEngine] Pushed ${this.state.currentScene} to stack, stack depth: ${this.sceneStack.length}`);
    }
    this.switchSceneWithTransition(newScene, 300);
  }

  /**
   * Pop previous scene from stack and return to it
   */
  popScene(): void {
    const previousScene = this.sceneStack.pop();
    if (previousScene) {
      console.log(`[GameEngine] Popped ${previousScene} from stack, remaining depth: ${this.sceneStack.length}`);
      this.switchSceneWithTransition(previousScene, 300);
    } else {
      console.warn('[GameEngine] No scene to pop from stack');
    }
  }

  /**
   * Clear scene stack (e.g., when returning to world from battle)
   */
  clearSceneStack(): void {
    console.log(`[GameEngine] Clearing scene stack (depth: ${this.sceneStack.length})`);
    this.sceneStack = [];
  }

  /**
   * Open menu from current scene (preserves return location)
   */
  openMenu(): void {
    console.log(`[GameEngine] Opening menu from ${this.state.currentScene}`);
    this.pushScene(GameScene.MENU);
  }

  /**
   * Close menu and return to previous scene
   */
  closeMenu(): void {
    console.log('[GameEngine] Closing menu');

    // Notify MenuScene that it's closing (for state sync callback)
    const menuScene = this.scenes.get(GameScene.MENU);
    if (menuScene && 'notifyMenuCloseExternal' in menuScene) {
      (menuScene as any).notifyMenuCloseExternal();
    }

    this.popScene();
  }

  // =========================================================================
  // Location-based Scene Management
  // =========================================================================

  /**
   * Enter a town
   */
  enterTown(townName: string): void {
    console.log(`[GameEngine] Entering town: ${townName}`);
    this.currentLocation = townName;
    this.returnScene = this.state.currentScene;
    this.switchSceneWithTransition(GameScene.TOWN, 500);
  }

  /**
   * Exit town and return to world
   */
  exitTown(): void {
    console.log('[GameEngine] Exiting town');
    this.currentLocation = null;
    this.switchSceneWithTransition(this.returnScene || GameScene.WORLD, 500);
    this.returnScene = null;
  }

  /**
   * Enter a dungeon
   */
  enterDungeon(dungeonName: string): void {
    console.log(`[GameEngine] Entering dungeon: ${dungeonName}`);
    this.currentLocation = dungeonName;
    this.returnScene = this.state.currentScene;
    this.switchSceneWithTransition(GameScene.DUNGEON, 500);
  }

  /**
   * Exit dungeon and return to world
   */
  exitDungeon(): void {
    console.log('[GameEngine] Exiting dungeon');
    this.currentLocation = null;
    this.switchSceneWithTransition(this.returnScene || GameScene.WORLD, 500);
    this.returnScene = null;
  }

  /**
   * Enter a shop from town
   */
  enterShop(shopName: string): void {
    console.log(`[GameEngine] Entering shop: ${shopName}`);
    this.pushScene(GameScene.SHOP);
  }

  /**
   * Exit shop and return to town
   */
  exitShop(): void {
    console.log('[GameEngine] Exiting shop');
    this.popScene();
  }

  /**
   * Enter an inn from town
   */
  enterInn(innName: string): void {
    console.log(`[GameEngine] Entering inn: ${innName}`);
    this.pushScene(GameScene.INN);
  }

  /**
   * Exit inn and return to town
   */
  exitInn(): void {
    console.log('[GameEngine] Exiting inn');
    this.popScene();
  }

  /**
   * Get current location name
   */
  getCurrentLocation(): string | null {
    return this.currentLocation;
  }

  // =========================================================================
  // Audio Management
  // =========================================================================

  /**
   * Update music based on current scene
   */
  private updateMusic(sceneType: GameScene): void {
    const track = SCENE_MUSIC[sceneType];
    if (track) {
      // Fade to new track if music is playing, otherwise just play it
      if (this.audio.isMusicPlaying()) {
        this.audio.fadeToMusic(track, 500, 500);
      } else {
        this.audio.playMusic(track);
      }
    }
  }

  /**
   * Get audio manager for scenes to play SFX
   */
  getAudio(): AudioManager {
    return this.audio;
  }

  /**
   * Toggle audio mute
   */
  toggleAudioMute(): void {
    this.audio.toggleMute();
  }

  /**
   * Set master volume (0-1)
   */
  setMasterVolume(volume: number): void {
    this.audio.setMasterVolume(volume);
  }

  /**
   * Set music volume (0-1)
   */
  setMusicVolume(volume: number): void {
    this.audio.setMusicVolume(volume);
  }

  /**
   * Set SFX volume (0-1)
   */
  setSFXVolume(volume: number): void {
    this.audio.setSFXVolume(volume);
  }

  // =========================================================================
  // World Position Management
  // =========================================================================

  /**
   * Set world position directly on WorldScene
   * Used to sync position from saved game state
   */
  setWorldPosition(x: number, y: number): void {
    const worldScene = this.scenes.get(GameScene.WORLD);
    if (worldScene && 'setPosition' in worldScene) {
      console.log('[GameEngine] setWorldPosition called:', x, y);
      (worldScene as any).setPosition(x, y);
    } else {
      console.warn('[GameEngine] setWorldPosition: WorldScene not available or missing setPosition');
    }
  }

  /**
   * Get a registered scene by type
   */
  getScene<T extends Scene>(sceneType: GameScene): T | undefined {
    return this.scenes.get(sceneType) as T | undefined;
  }

  // =========================================================================
  // Cleanup
  // =========================================================================

  destroy(): void {
    this.stop();

    if (this.currentScene?.cleanup) {
      this.currentScene.cleanup();
    }

    // Cleanup audio
    this.audio.dispose();

    window.removeEventListener('keydown', this.handleKeyDown.bind(this));
    window.removeEventListener('keyup', this.handleKeyUp.bind(this));
  }
}
